/**
 * Build Client Snapshot from questionnaire data for ItineraryView.
 * Schema v1: chips (cap 12), grouped pinned notes, optional flags.
 * Never errors on missing questionnaireData; returns empty snapshot.
 */

const TRUNCATE_LEN = 140;

/** Format YYYY-MM-DD (or ISO) to MM/dd/yyyy for display (e.g. 07/06/2026). */
function formatDateMmDdYyyy(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${m}/${d}/${y}`;
  }
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return s;
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  const year = dt.getFullYear();
  return `${month}/${day}/${year}`;
}

function truncate(str) {
  if (typeof str !== "string") return "";
  const s = str.trim();
  if (s.length <= TRUNCATE_LEN) return s;
  return s.slice(0, TRUNCATE_LEN).trim() + "…";
}

function safeArray(val) {
  if (Array.isArray(val)) return val;
  if (val != null && typeof val === "string") return [val];
  return [];
}

function safeStr(val) {
  if (val == null) return "";
  return String(val).trim();
}

/**
 * @param {Object} questionnaireData - Nested questionnaire payload (may be {} or legacy with budget)
 * @returns {{ chips: Array<{key: string, label: string, priority: number}>, sections: Array<{title: string, bullets: string[]}>, flags: string[] }}
 */
export function buildClientSnapshot(questionnaireData) {
  const chips = [];
  const sections = [];
  const flags = [];

  if (!questionnaireData || typeof questionnaireData !== "object") {
    return { chips, sections, flags };
  }

  const q = questionnaireData;
  const basic = q.basicInformation || {};
  const group = q.groupInfo || {};
  const ticket = q.parkTicketAccess || {};
  const accommodation = q.accommodation || {};
  const ride = q.attractionRideStrategy || {};
  const time = q.timeEnergy || {};
  const dining = q.diningPreferences || {};
  const show = q.showFireworkStrategy || {};
  const specialty = q.specialtyExperiences || {};
  const special = q.specialConsiderations || {};
  const characters = q.characterMeetGreets || {};
  const parks = q.parkVisitPlans || {};

  // ---------- Chips (cap 12, ordered by priority) ----------
  const hopper = safeStr(ticket.hasParkHopper);
  if (hopper && hopper !== "No") chips.push({ key: "hopper", label: "Park Hopper", priority: 1 });
  const ticketType = safeStr(ticket.ticketType);
  if (ticketType) chips.push({ key: "ticketType", label: ticketType, priority: 2 });
  const transport = safeStr(accommodation.transportation);
  if (transport) chips.push({ key: "transport", label: transport, priority: 3 });
  const staying = safeStr(accommodation.stayingOnProperty);
  const resort = safeStr(accommodation.resort) || safeStr(accommodation.otherHotel);
  if (staying) {
    const chipLabel = resort ? `${staying} • ${truncate(resort)}` : staying;
    chips.push({ key: "accommodation", label: chipLabel, priority: 4 });
  } else if (resort) {
    chips.push({ key: "resort", label: truncate(resort), priority: 4 });
  }
  const adults = group.adults != null ? String(group.adults) : "";
  if (adults) chips.push({ key: "adults", label: `${adults} Adult${adults !== "1" ? "s" : ""}`, priority: 5 });
  const kids = group.kids != null ? String(group.kids) : "";
  if (kids && kids !== "0") chips.push({ key: "kids", label: `${kids} Kid${kids !== "1" ? "s" : ""}`, priority: 6 });
  const agesBoys = safeArray(group.agesOfBoys);
  const agesGirls = safeArray(group.agesOfGirls);
  const agesAll = [...agesBoys, ...agesGirls].filter(Boolean).map(String);
  if (agesAll.length) {
    const min = Math.min(...agesAll.map((a) => parseInt(a, 10)).filter((n) => !isNaN(n)));
    const max = Math.max(...agesAll.map((a) => parseInt(a, 10)).filter((n) => !isNaN(n)));
    if (!isNaN(min) && !isNaN(max)) chips.push({ key: "kidsAges", label: `Ages ${min}–${max}`, priority: 7 });
  }
  const parkStart = safeArray(time.parkStartTime);
  if (parkStart.length) chips.push({ key: "parkStart", label: parkStart.slice(0, 2).join(", "), priority: 8 });
  const breakPlan = safeArray(time.breakTime);
  if (breakPlan.length) chips.push({ key: "breakPlan", label: breakPlan.slice(0, 2).join(", "), priority: 9 });
  const ll = safeStr(ride.lightningLane);
  if (ll && ll !== "No") chips.push({ key: "lightningLane", label: ll, priority: 10 });
  const allergies = safeArray(dining.allergies).filter((a) => a && a !== "None");
  if (allergies.length) {
    chips.push({
      key: "allergies",
      label: allergies.length > 1 ? `${allergies.length} Allergies` : allergies[0],
      priority: 11,
    });
  }
  const hasMobility = special.hasDisability === "Yes" || safeStr(special.disabilities);
  if (hasMobility) chips.push({ key: "mobility", label: "Mobility considerations", priority: 12 });

  chips.sort((a, b) => a.priority - b.priority);
  const cappedChips = chips.slice(0, 12);

  // ---------- Pinned notes sections ----------
  // Booking Constraints (dates, resort, transport) — dates as MM/dd/yyyy
  const startDateRaw = safeStr(basic.startDate);
  const endDateRaw = safeStr(basic.endDate);
  const bookingBullets = [];
  if (startDateRaw && endDateRaw) {
    const startFormatted = formatDateMmDdYyyy(startDateRaw);
    const endFormatted = formatDateMmDdYyyy(endDateRaw);
    if (startFormatted && endFormatted) bookingBullets.push(`Trip: ${startFormatted} – ${endFormatted}`);
  }
  if (staying || resort) bookingBullets.push(truncate(resort || staying));
  if (transport) bookingBullets.push(transport);
  if (bookingBullets.length) sections.push({ title: "Booking Constraints", bullets: bookingBullets });

  // Time & Pacing
  const timeBullets = [];
  if (parkStart.length) timeBullets.push(`Park start: ${parkStart.join(", ")}`);
  if (breakPlan.length) timeBullets.push(`Break plan: ${breakPlan.join(", ")}`);
  if (timeBullets.length) sections.push({ title: "Time & Pacing", bullets: timeBullets });

  // Dining (allergies prominent)
  const diningBullets = [];
  if (allergies.length) diningBullets.push(`Allergies: ${allergies.join(", ")}`);
  const restStrategy = safeArray(dining.restaurantStrategy);
  if (restStrategy.length) diningBullets.push(`Preferences: ${restStrategy.join(", ")}`);
  const diningTime = safeArray(dining.diningTime);
  if (diningTime.length) diningBullets.push(`Dining time: ${diningTime.join(", ")}`);
  if (diningBullets.length) sections.push({ title: "Dining", bullets: diningBullets });

  // Priorities (ride strategy, must rides/shows, premium)
  const priorBullets = [];
  const rideStrategy = safeArray(ride.rideStrategy);
  if (rideStrategy.length) priorBullets.push(`Ride strategy: ${rideStrategy.join(", ")}`);
  const mustRides = safeArray(ride.mustRides);
  if (mustRides.length) priorBullets.push(`Must rides: ${truncate(mustRides.join(", "))}`);
  const mustShows = safeArray(show.mustSeeShows);
  if (mustShows.length) priorBullets.push(`Must shows: ${truncate(mustShows.join(", "))}`);
  const showStrategy = safeArray(show.showStrategy);
  if (showStrategy.length) priorBullets.push(`Show strategy: ${showStrategy.join(", ")}`);
  const enchanted = safeStr(specialty.enchantedExperiences);
  const mustExp = safeArray(specialty.mustExperience);
  if (enchanted === "Yes" && mustExp.length) priorBullets.push(`Premium: ${truncate(mustExp.join(", "))}`);
  if (priorBullets.length) sections.push({ title: "Priorities", bullets: priorBullets });

  // Special Considerations (mobility, celebration, additional)
  const specBullets = [];
  if (safeStr(special.disabilities)) specBullets.push(truncate(special.disabilities));
  if (special.celebration === "Yes" && safeStr(special.celebrationDetails)) {
    specBullets.push(`Celebration: ${truncate(special.celebrationDetails)}`);
  }
  if (special.additionalInfo === "Yes" && safeStr(special.additionalInfoDetails)) {
    specBullets.push(truncate(special.additionalInfoDetails));
  }
  if (specBullets.length) sections.push({ title: "Special Considerations", bullets: specBullets });

  // Characters
  const charInterest = safeStr(characters.interestedInCharacterMeetGreets);
  const mustMeet = safeArray(characters.mustMeetCharacters);
  const charOther = safeStr(characters.mustMeetCharactersOther);
  if (charInterest === "Yes" && (mustMeet.length || charOther)) {
    const charBullets = mustMeet.length ? [mustMeet.join(", ")] : [];
    if (charOther) charBullets.push(charOther);
    sections.push({ title: "Characters", bullets: charBullets.map(truncate) });
  }

  // ---------- Flags (0–3 short warnings) ----------
  if (allergies.length) flags.push("Allergies noted");
  if (hasMobility) flags.push("Mobility considerations");
  if (safeArray(ride.mustRides).length > 15) flags.push("Many must-do rides");
  const finalFlags = flags.slice(0, 3);

  return { chips: cappedChips, sections, flags: finalFlags };
}
